import React, { useEffect, useState } from "react";
import {
  Badge,
  HStack,
  Heading,
  IconButton,
  Tooltip,
  useToast,
} from "@chakra-ui/react";
import {
  CInput,
  CSelect,
  ProcessDeleteionRequestModal,
  ViewCOCDeleteRequestModal,
} from "../components";
import DataTable from "react-data-table-component";
import { AiFillEye } from "react-icons/ai";
import { FaCheck, FaXmark } from "react-icons/fa6";
import { CocDeleteRequestModel, CocModel } from "../services/models";

export default function COCDeleteRequests() {
  const [pending, setPending] = useState(true);
  const [requests, setRequests] = useState([]);
  const [filteredRequests, setFilteredRequests] = useState([]);
  const [searchCompany, setSearchCompany] = useState("");
  const [searchStatus, setSearchStatus] = useState(null);
  const toast = useToast();
  const statusOptions = [
    { label: "Pending", value: "pending" },
    { label: "Approved", value: "approved" },
    { label: "Disapproved", value: "disapproved" },
  ];
  const getStatusColor = (status) => {
    switch (status) {
      case "pending":
        return "yellow";
      case "approved":
        return "green";
      case "disapproved":
        return "red";
      default:
        return "yellow";
    }
  };
  const customStyles = {
    headRow: {
      style: {
        borderBottomWidth: "0px",
      },
    },
    headCells: {
      style: {
        fontSize: "14px",
        fontWeight: "bold",
      },
    },
    rows: {
      style: {
        borderRadius: "15px",
        boxShadow: "1px 2px 6px 0px rgb(0,0,0,0.1)",
        marginBottom: "10px",
        "&:not(:last-of-type)": {
          borderBottomWidth: "0px",
        },
      },
    },
    pagination: {
      style: {
        borderTopWidth: "0px",
      },
    },
  };
  const columns = [
    {
      name: "Company Name",
      selector: (row) =>
        row.status === "pending" ? row.coc.clientName : "N/A",
    },
    {
      name: "Client Email",
      selector: (row) => (row.status === "pending" ? row.coc.email : "N/A"),
    },
    {
      name: "Request Status",
      cell: (row) => {
        return (
          <Badge
            colorScheme={getStatusColor(row.status)}
            rounded="full"
            textTransform="capitalize"
            px="1.5"
            pb="0.5"
          >
            {row.status}
          </Badge>
        );
      },
    },
    {
      name: "Comment",
      selector: (row) => row.comment,
    },
    {
      name: "Action",
      cell: (row) => {
        return (
          <HStack>
            <ViewCOCDeleteRequestModal data={row} />
            {row.status === "pending" && (
              <>
                <ProcessDeleteionRequestModal
                  data={row}
                  updateValue="Approve"
                  onProcess={onProcess}
                />
                <ProcessDeleteionRequestModal
                  data={row}
                  updateValue="Disapprove"
                  onProcess={onProcess}
                />
              </>
            )}
          </HStack>
        );
      },
    },
  ];

  const onProcess = async (data, updateValue) => {
    try {
      let response;
      if (updateValue === "Approve") {
        response = await CocModel.delete(data.coc._id);
        if (response.status === 200) {
          let body = {
            coc: data.coc._id,
            status: "approved",
            comment: data.comment,
          };
          response = await CocDeleteRequestModel.update(data._id, body);
        }
      } else {
        if (!data.coc.dateReceived) {
          data.coc.dateReceived = "";
        }
        response = await CocModel.update(data.coc._id, {
          ...data.coc,
          status: "pending",
        });
        let body = {
          coc: data.coc._id,
          status: "disapproved",
          comment: data.comment,
        };
        response = await CocDeleteRequestModel.update(data._id, body);
      }
      let newData = [...requests];
      let foundIndex = newData.findIndex((item) => item._id === data._id);
      newData[foundIndex].status =
        updateValue === "Approve" ? "approved" : "disapproved";
      setRequests(newData);
      newData = [...filteredRequests];
      foundIndex = newData.findIndex((item) => item._id === data._id);
      newData[foundIndex].status =
        updateValue === "Approve" ? "approved" : "disapproved";
      setFilteredRequests(newData);
      toast({
        description: "Request processed successfully!",
        status: "success",
        isClosable: "true",
      });
    } catch (error) {
      toast({
        description: "Something went wrong!",
        status: "error",
        isClosable: "true",
      });
    }
  };

  useEffect(() => {
    const fetchRequests = async () => {
      try {
        let response = await CocDeleteRequestModel.get();
        setRequests(response.data.data);
        setFilteredRequests(response.data.data);
        console.log(response.data.data);
      } catch (error) {
        toast({
          description: "Something went wrong!",
          status: "error",
          isClosable: true,
        });
      } finally {
        setPending(false);
      }
    };
    fetchRequests();
  }, []);

  const filterMethods = [
    (item) =>
      item.coc.clientName.toLowerCase().indexOf(searchCompany?.toLowerCase()) !=
      -1,
    (item) => (searchStatus !== null ? item.status === searchStatus : true),
  ];

  useEffect(() => {
    if (searchCompany || searchStatus) {
      let searchedRequests = requests.filter((item) => {
        for (let i = 0; i < filterMethods.length; i++) {
          if (!filterMethods[i](item)) {
            return false;
          }
        }
        return true;
      });
      setFilteredRequests(searchedRequests);
    } else {
      setFilteredRequests(requests);
    }
  }, [searchCompany, searchStatus]);

  return (
    <>
      <HStack
        justifyContent="space-between"
        alignItems={{ base: "start", md: "center" }}
        flexDirection={{ base: "column", md: "row" }}
        mb="2"
      >
        <Heading fontSize={{ base: "xl", md: "3xl" }}>
          All COC Deletion Requests
        </Heading>
        <HStack
          gap="2"
          minW={{ base: "100%", md: "xl" }}
          flexDirection={{ base: "column", md: "row" }}
        >
          <CInput
            placeholder="Search Company Name"
            value={searchCompany}
            onChange={(e) => setSearchCompany(e.target.value)}
          />
          <CSelect
            placeholder="Request Status"
            options={statusOptions}
            onChange={(opt) => setSearchStatus(opt ? opt.value : null)}
            isClearable={true}
          />
        </HStack>
      </HStack>
      <DataTable
        columns={columns}
        data={filteredRequests}
        progressPending={pending}
        keyField="_id"
        customStyles={customStyles}
        responsive
        pagination
      />
    </>
  );
}
